import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { navLinks } from '../data/mockData';
import { ChevronDown, ChevronUp, X, Menu } from 'lucide-react';

const AlexLogo = () => (
  <div className="flex items-center gap-2.5">
    <svg width="26" height="32" viewBox="0 0 26 32" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M13 0C13 0 0 15.5 0 22.5C0 27.75 5.82 32 13 32C20.18 32 26 27.75 26 22.5C26 15.5 13 0 13 0Z" fill="#082900"/>
      <path d="M13 8C13 8 4.5 19 4.5 23.5C4.5 27.09 8.3 30 13 30C17.7 30 21.5 27.09 21.5 23.5C21.5 19 13 8 13 8Z" fill="#F9FBF5" opacity="0.3"/>
      <path d="M13 15C13 15 8 22.5 8 25C8 27.76 10.24 29 13 29C15.76 29 18 27.76 18 25C18 22.5 13 15 13 15Z" fill="#F9FBF5" opacity="0.6"/>
    </svg>
    <span className="text-[#082900] text-[22px] font-semibold tracking-[-0.02em] leading-none">Duty AI</span>
  </div>
);

/* ── Products Mega Menu (centered in viewport, responsive) ── */
const ProductsMegaMenu = ({ link, onClose }) => {
  return (
    <div className="fixed left-1/2 -translate-x-1/2 w-[calc(100vw-32px)] max-w-[920px] bg-white rounded-2xl shadow-xl border border-[#082900]/8 z-50 overflow-hidden"
         style={{ top: 'auto' }}>
      {/* Content */}
      <div className="flex flex-col lg:flex-row p-6 lg:p-8 gap-6 lg:gap-8">
        {/* Left columns (Meet Duty AI + Channels) */}
        <div className="flex flex-col sm:flex-row gap-6 sm:gap-10 flex-1">
          {link.columns.map((col, ci) => (
            <div key={ci} className="flex-1 min-w-0">
              <p className="text-[#082900]/50 text-[13px] font-normal mb-4 lg:mb-5">{col.heading}</p>
              <div className="space-y-4 lg:space-y-5">
                {col.items.map((item, ii) => (
                  <Link
                    key={ii}
                    to={item.href}
                    className="block group"
                    onClick={onClose}
                  >
                    <span className="text-[#082900] text-[14px] lg:text-[15px] font-semibold leading-tight group-hover:underline">
                      {item.label}
                    </span>
                    <span className="block text-[#082900]/50 text-[12px] lg:text-[13px] font-normal leading-snug mt-0.5">
                      {item.desc}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Featured / Case Study - hidden on smaller tablets */}
        {link.featured && (
          <div className="hidden lg:block w-[260px] flex-shrink-0">
            <div className="bg-[#EAE5D9] rounded-xl overflow-hidden">
              <div className="relative">
                <div className="absolute top-4 left-4 z-10">
                  <p className="text-[#082900] text-[20px] font-light leading-tight">
                    {link.featured.name}
                  </p>
                  <p className="text-[#082900]/60 text-[13px] font-normal mt-0.5">
                    {link.featured.title}
                  </p>
                </div>
                <img
                  src={link.featured.image}
                  alt={link.featured.name}
                  className="w-full h-[200px] object-cover"
                  loading="lazy"
                />
              </div>
              <div className="px-4 py-3">
                <img
                  src={link.featured.logo}
                  alt="Company"
                  className="h-10 w-auto object-contain"
                  loading="lazy"
                />
              </div>
            </div>
            <Link
              to={link.featured.caseStudyHref}
              className="block mt-3 group"
              onClick={onClose}
            >
              <span className="text-[#082900]/50 text-[12px] font-normal">
                {link.featured.caseStudyLabel}
              </span>
              <span className="block text-[#082900] text-[14px] font-normal leading-snug mt-0.5 group-hover:underline">
                {link.featured.caseStudyTitle}
              </span>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

/* ── Simple Dropdown (Resources) – centered under button ── */
const SimpleDropdown = ({ link, onClose }) => {
  return (
    <div className="absolute top-full right-0 mt-3 bg-white rounded-xl shadow-xl border border-[#082900]/8 py-3 min-w-[260px] z-50">
      {link.items.map((item, j) => (
        <Link
          key={j}
          to={item.href}
          className="block px-5 py-2.5 group hover:bg-[#F9FBF5] transition-colors"
          onClick={onClose}
        >
          <span className="text-[#082900] text-[14px] font-semibold group-hover:underline">
            {item.label}
          </span>
          {item.desc && (
            <span className="block text-[#082900]/50 text-[12px] font-normal mt-0.5">
              {item.desc}
            </span>
          )}
        </Link>
      ))}
    </div>
  );
};

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileAccordion, setMobileAccordion] = useState(null);

  const [megaTop, setMegaTop] = useState(0);
  const navRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleDropdown = useCallback((i) => {
    setOpenDropdown((prev) => {
      const next = prev === i ? null : i;
      if (next !== null && navRef.current) {
        const rect = navRef.current.getBoundingClientRect();
        setMegaTop(rect.bottom + 4);
      }
      return next;
    });
  }, []);

  const closeDropdown = () => setOpenDropdown(null);

  return (
    <>
      <nav
        ref={navRef}
        className={`sticky top-0 z-40 transition-all duration-300 ${
          scrolled ? 'bg-[#F9FBF5]/95 backdrop-blur-md shadow-sm' : 'bg-[#F9FBF5]'
        }`}
      >
        <div className="max-w-[1121px] mx-auto px-6 lg:px-10">
          <div className="flex items-center justify-between h-[72px]">
            <Link to="/" className="flex-shrink-0">
              <AlexLogo />
            </Link>

            {/* Desktop Nav Links */}
            <div className="hidden lg:flex items-center gap-8" ref={dropdownRef}>
              {navLinks.map((link, i) => (
                <div key={i} className="relative">
                  {link.hasDropdown ? (
                    <button
                      onClick={() => toggleDropdown(i)}
                      className={`flex items-center gap-1 text-[15px] font-medium transition-opacity ${
                        openDropdown === i ? 'text-[#082900]' : 'text-[#082900] hover:opacity-70'
                      }`}
                    >
                      {link.label}
                      {openDropdown === i ? (
                        <ChevronUp className="w-4 h-4" />
                      ) : (
                        <ChevronDown className="w-4 h-4" />
                      )}
                    </button>
                  ) : (
                    <Link
                      to={link.href}
                      className="text-[#082900] text-[15px] font-medium hover:opacity-70 transition-opacity"
                    >
                      {link.label}
                    </Link>
                  )}

                  {/* Simple dropdown (Resources) stays relative to button */}
                  {link.hasDropdown && !link.megaMenu && openDropdown === i && (
                    <SimpleDropdown link={link} onClose={closeDropdown} />
                  )}
                </div>
              ))}

              {/* Products Mega Menu — rendered as portal-like fixed element */}
              {navLinks.map((link, i) =>
                link.hasDropdown && link.megaMenu && openDropdown === i ? (
                  <div key={`mega-${i}`} style={{ position: 'fixed', top: megaTop, left: 0, right: 0, zIndex: 50, pointerEvents: 'none' }}>
                    <div style={{ pointerEvents: 'auto' }}>
                      <ProductsMegaMenu link={link} onClose={closeDropdown} />
                    </div>
                  </div>
                ) : null
              )}
            </div>

            {/* CTA Buttons */}
            <div className="hidden lg:flex items-center gap-3">
              <Link
                to="/dashboard"
                className="px-3 py-2.5 text-[#082900] text-[12px] font-semibold tracking-[1px] uppercase hover:opacity-70 transition-opacity duration-300"
              >
                For recruiters
              </Link>
              <Link
                to="/login"
                className="px-5 py-2.5 text-[#082900] text-[12px] font-semibold tracking-[1px] uppercase border border-[#082900] rounded-full hover:bg-[#082900] hover:text-[#EEF2E1] transition-colors duration-300"
              >
                Log in
              </Link>
              <Link
                to="/explore"
                className="px-5 py-2.5 bg-[#082900] text-[#EEF2E1] text-[12px] font-semibold tracking-[1px] uppercase rounded-full hover:bg-[#0A3900] transition-colors duration-300"
              >
                Dashboard
              </Link>
            </div>

            <button
              className="lg:hidden text-[#082900]"
              onClick={() => setMobileOpen(!mobileOpen)}
            >
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div className="lg:hidden bg-[#F9FBF5] border-t border-[#082900]/10 px-6 py-4 max-h-[80vh] overflow-y-auto">
            {navLinks.map((link, i) => (
              <div key={i} className="py-3 border-b border-[#082900]/10 last:border-0">
                {link.hasDropdown ? (
                  <>
                    <button
                      onClick={() => setMobileAccordion(mobileAccordion === i ? null : i)}
                      className="flex items-center justify-between w-full text-[#082900] text-[16px] font-medium"
                    >
                      {link.label}
                      <ChevronDown className={`w-4 h-4 transition-transform ${mobileAccordion === i ? 'rotate-180' : ''}`} />
                    </button>
                    {mobileAccordion === i && (
                      <div className="mt-3 pl-2 space-y-1">
                        {link.megaMenu ? (
                          link.columns.map((col, ci) => (
                            <div key={ci} className="mb-4">
                              <p className="text-[#082900]/40 text-[12px] font-normal uppercase tracking-wider mb-2">{col.heading}</p>
                              {col.items.map((item, ii) => (
                                <Link
                                  key={ii}
                                  to={item.href}
                                  className="block py-2"
                                  onClick={() => setMobileOpen(false)}
                                >
                                  <span className="text-[#082900] text-[14px] font-semibold">{item.label}</span>
                                  <span className="block text-[#082900]/50 text-[12px] font-normal">{item.desc}</span>
                                </Link>
                              ))}
                            </div>
                          ))
                        ) : (
                          link.items.map((item, j) => (
                            <Link
                              key={j}
                              to={item.href}
                              className="block py-2"
                              onClick={() => setMobileOpen(false)}
                            >
                              <span className="text-[#082900] text-[14px] font-semibold">{item.label}</span>
                              {item.desc && (
                                <span className="block text-[#082900]/50 text-[12px] font-normal">{item.desc}</span>
                              )}
                            </Link>
                          ))
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <Link to={link.href} className="text-[#082900] text-[16px] font-medium" onClick={() => setMobileOpen(false)}>
                    {link.label}
                  </Link>
                )}
              </div>
            ))}
            <div className="flex flex-col gap-3 mt-4">
              <Link to="/login" className="text-center px-5 py-2.5 text-[#082900] text-[12px] font-semibold tracking-[1px] uppercase border border-[#082900] rounded-full">
                Log in
              </Link>
              <Link to="/explore" className="text-center px-5 py-2.5 bg-[#082900] text-[#EEF2E1] text-[12px] font-semibold tracking-[1px] uppercase rounded-full" onClick={() => setMobileOpen(false)}>
                Dashboard
              </Link>
            </div>
          </div>
        )}
      </nav>

      {/* Backdrop overlay when mega menu is open */}
      {openDropdown !== null && navLinks[openDropdown]?.megaMenu && (
        <div
          className="fixed inset-0 z-30 bg-black/5"
          onClick={closeDropdown}
        />
      )}
    </>
  );
};

export default Navbar;
